import React, {useEffect, useState} from 'react';

import {ReactComponent as AcademicSvg} from '../assets/SVG/ic-academic.svg';
import {ReactComponent as OrganizationSvg} from '../assets/SVG/ic-organization.svg';
import {ReactComponent as PersonalSvg} from '../assets/SVG/ic-personal.svg';
import {Email, EmailType} from '../generated/graphql';
import * as OS from '../pages/OnboardingFlow.styles';
import * as S from './WBUsageTypeSelection.styles';

export enum UsageType {
  Personal = 'personal',
  Corporate = 'corporate',
  Academic = 'academic',
}

type Props = {
  verifiedViewerEmailDomainTypes?: EmailType[];
  viewerEmails?: Email[];
  onUsageTypeChange: (usageType: UsageType) => void;
  onClickContinue: (needsToAddWorkEmail: boolean) => void;
  isTransitioningPage: boolean;
};

export const WBUsageTypeSelection = ({
  verifiedViewerEmailDomainTypes,
  viewerEmails,
  onUsageTypeChange,
  onClickContinue,
  isTransitioningPage,
}: Props) => {
  const hasVerifiedCorporateEmail = verifiedViewerEmailDomainTypes?.includes(
    EmailType.Corporate
  );
  const [usageType, setUsageType] = useState<UsageType | null>(null);

  useEffect(() => {
    if (usageType == null) {
      return;
    }
    try {
      onUsageTypeChange(usageType);
    } catch (e) {
      console.error(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usageType]);

  const usageTypeOptions = [
    {
      label: 'Work',
      value: UsageType.Corporate,
      description: 'Use W&B to collaborate with your colleagues at work.',
    },
    {
      label: 'Academic',
      value: UsageType.Academic,
      description: 'Use W&B for research or education.',
    },
    {
      label: 'Personal',
      value: UsageType.Personal,
      description: 'Use W&B for personal projects without team collaboration.',
    },
  ];

  const handleClickContinue = () => {
    const needsToAddWorkEmail =
      usageType === UsageType.Corporate && !hasVerifiedCorporateEmail;
    onClickContinue(needsToAddWorkEmail);
  };

  // TODO: revert to WBIcon usage when we fix SVG export from Figma
  // const usageTypeKeyToIconNameMap = {
  //   [UsageType.Corporate]: 'organization',
  //   [UsageType.Academic]: 'academic',
  //   [UsageType.Personal]: 'personal',
  // };

  const usageTypeKeyToSvg = {
    [UsageType.Corporate]: OrganizationSvg,
    [UsageType.Academic]: AcademicSvg,
    [UsageType.Personal]: PersonalSvg,
  };

  return (
    <>
      <S.MainContainer>
        {usageTypeOptions.map(({label, value, description}) => (
          <S.SelectionItemContainer
            key={value}
            data-test={`usage-type-${value}`}
            $selected={usageType === value}
            onClick={() => setUsageType(value)}>
            <S.IconAndTextContainer>
              <S.IconContainer $selected={usageType === value}>
                {/* TODO: revert to WBIcon usage when we fix SVG export from Figma */}
                {/* <S.Icon name={usageTypeKeyToIconNameMap[value]} /> */}
                {/* <img src={usageTypeKeyToSvg[value]} alt={value} /> */}
                {React.createElement(usageTypeKeyToSvg[value])}
              </S.IconContainer>
              <S.ItemLabelAndDescriptionContainer>
                <S.ItemLabel>
                  <div>{label}</div>
                  {value === UsageType.Corporate && (
                    <S.Label>Most Popular</S.Label>
                  )}
                </S.ItemLabel>
                <S.ItemDescription>{description}</S.ItemDescription>
              </S.ItemLabelAndDescriptionContainer>
            </S.IconAndTextContainer>
            <S.CheckboxContainer>
              <S.CheckMarkIcon
                $selected={usageType === value}
                name="heavy-checkmark"
              />
            </S.CheckboxContainer>
          </S.SelectionItemContainer>
        ))}
      </S.MainContainer>
      <OS.SingleDialogButton
        data-test="continue-button"
        primary
        disabled={usageType == null || isTransitioningPage}
        loading={isTransitioningPage}
        onClick={() => {
          handleClickContinue();
        }}>
        Continue
      </OS.SingleDialogButton>
    </>
  );
};
